/**
 * #109 · Bandeja de pedidos de rollos.
 *
 * Lo que los clientes le piden a XYVONA. La API los devuelve con los
 * pendientes primero y los más viejos arriba: una bandeja ordenada por fecha
 * descendente esconde justamente el pedido que más esperó.
 *
 * Resolver un pedido no genera nada por sí solo. El circuito real es:
 * aprobar → generar el stock en Etiquetas → entregar el rollo en Stock →
 * marcar entregado. Por eso los tres estados están separados y "entregado" es
 * una acción aparte de "aprobado".
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { useApiQuery } from '@/api/hooks';
import type { MessageKey } from '@/i18n/messages';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Badge, type Tone } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { QueryBoundary } from '@/ui/states';
import { Table, Td, Th } from '@/ui/Table';
import { hqKeys } from './keys';

type Pedido = Schemas['RollRequestOut'];

const TONO: Record<string, Tone> = {
  pending: 'progress',
  approved: 'neutral',
  fulfilled: 'neutral',
  rejected: 'critical',
};

/**
 * Los cuatro estados de `roll_request_status_ck`. El `Record<string, …>` es a
 * propósito: `status` llega como string del OpenAPI, y un estado que la base
 * acepte y esta tabla no conozca tiene que caer en algo legible en vez de
 * dejar la celda vacía.
 */
const ETIQUETA: Record<string, MessageKey> = {
  pending: 'requests.state.pending',
  approved: 'requests.state.approved',
  fulfilled: 'requests.state.fulfilled',
  rejected: 'requests.state.rejected',
};

export function RequestsPage(): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();
  const [soloPendientes, setSoloPendientes] = useState(true);

  const pedidos = useApiQuery<Pedido[]>(
    hqKeys.requests(soloPendientes ? 'pending' : ''),
    '/api/v1/backoffice/roll-requests',
    soloPendientes ? { estado: 'pending' } : undefined,
  );

  const resolver = useMutation<unknown, unknown, { id: number; status: string }>({
    mutationFn: ({ id, status }) =>
      api.post(`/api/v1/backoffice/roll-requests/${id}/resolve`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: hqKeys.all });
    },
  });

  return (
    <div className="mx-auto max-w-6xl">
      <Card>
        <CardHeader
          title={t('requests.title')}
          description={t('requests.description')}
          actions={
            <Button size="sm" variant="ghost" onClick={() => { setSoloPendientes((v) => !v); }}>
              {t(soloPendientes ? 'requests.filter.all' : 'requests.filter.pending')}
            </Button>
          }
        />
        <CardBody className="p-0">
          <QueryBoundary query={pedidos} empty={{ title: t('requests.empty') }}>
            {(filas) => (
              <Table caption={t('requests.title')}>
                <thead>
                  <tr>
                    <Th>{t('requests.col.client')}</Th>
                    <Th className="text-right">{t('requests.col.quantity')}</Th>
                    <Th>{t('requests.col.neededBy')}</Th>
                    <Th>{t('requests.col.notes')}</Th>
                    <Th>{t('requests.col.state')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {filas.map((fila) => (
                    <tr key={fila.request_id}>
                      <Td>
                        <span className="font-medium text-ink">
                          {fila.tenant_name ?? fila.tenant_slug}
                        </span>
                        <span className="block text-xs text-ink-muted">
                          {fmt.relative(fila.created_at)}
                        </span>
                      </Td>
                      <Td className="text-right tabular-nums">{fmt.number(fila.quantity)}</Td>
                      <Td className="text-xs">
                        {fila.needed_by === null ? '—' : fmt.date(fila.needed_by)}
                      </Td>
                      <Td className="max-w-xs truncate text-xs text-ink-muted">
                        {fila.notes ?? '—'}
                      </Td>
                      <Td>
                        <Badge tone={TONO[fila.status] ?? 'neutral'}>
                          {t(ETIQUETA[fila.status] ?? 'requests.state.pending')}
                        </Badge>
                      </Td>
                      <Td className="text-right">
                        {fila.status === 'pending' && (
                          <span className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={resolver.isPending}
                              onClick={() =>
                                { resolver.mutate({ id: fila.request_id, status: 'rejected' }); }
                              }
                            >
                              {t('requests.reject')}
                            </Button>
                            <Button
                              size="sm"
                              variant="primary"
                              disabled={resolver.isPending}
                              onClick={() =>
                                { resolver.mutate({ id: fila.request_id, status: 'approved' }); }
                              }
                            >
                              {t('requests.approve')}
                            </Button>
                          </span>
                        )}
                        {fila.status === 'approved' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={resolver.isPending}
                            onClick={() =>
                              { resolver.mutate({ id: fila.request_id, status: 'fulfilled' }); }
                            }
                          >
                            {t('requests.fulfill')}
                          </Button>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </QueryBoundary>
        </CardBody>
      </Card>
    </div>
  );
}
